import React, { useState, useEffect, useRef } from 'react';
import '../view-archer-styles.css';

//material UI
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography, Paper, Popper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField} from '@mui/material/';
import { Check, Drafts, Add, Email, DeleteOutline, EditNote, ChatBubbleOutline, DriveFileRenameOutline} from '@mui/icons-material/';

//components import
import FlaggedItem from '../dataObjects/FlaggedItem';
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import AlertInfo from '../../../dataObjects/AlertInfo';

interface FlagItemsTableProps {
    flagItems: FlaggedItem[];
    removeFlaggedItem: (flaggedItemId: number) => void;
    updateFlaggedItems: () => void;
    isGroupUiEditable?: boolean; //excess contribution issues only
    cpfId?: number;
    auditPeriodId?: string; //ie 2026-AUG; needed to look up the audit sheet's excess UIs
}

//Gateway issue category of excess contribution issues (110, 112, 136...), the only ones whose group UI can be edited
export const excessContributionCategoryId = 5;

//Same format spDEPAUDITS_TAG_SAVE numbers excess groups with, ie EXCESS-2026-1
const groupUiPattern = /^EXCESS-\d{4}-\d{1,4}$/;

const FlagItemsTable = (props: FlagItemsTableProps) => {

    const removeFlaggedItem = (flaggedItemId: number) => {
        props.removeFlaggedItem(flaggedItemId);
    }

    const [selectedItem, setSelectedItem] = useState<FlaggedItem>();

    /*ITEM AUDIT NOTE*/
    const [itemAuditNote, setItemAuditNote] = useState('');
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const openItemAuditNoteDialog = (event: React.MouseEvent<HTMLElement>, item: FlaggedItem) => {
        setSelectedItem(item);
        setItemAuditNote(item.auditorNote);
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };

    const isAuditNoteDialogOpen = Boolean(anchorEl);

    const saveItemAuditNote = () => {

        if (itemAuditNote.length > 0) {
            var params = [
                `flaggedItemId=${selectedItem?.flaggedItemId}`,
                `note=${itemAuditNote}`
            ].join('&');
            let url = `/Archer3/SaveFlaggedItemNote?${params}`
            fetch(url)
                .then(result => result.json())
                .then(result => {
                    if (result.isError) {
                        let info = {
                            type: 'error',
                            title: 'Error: SaveFlaggedItemNote',
                            errorMessage: result.errorMessage,
                            secondaryErrorMessage: result.secondaryErrorMessage
                        };
                        setAlertInfo(info);
                        setAlertInfoDialog(true);
                    }
                    else {
                        setItemAuditNote('');
                        setAnchorEl(null);
                        props.updateFlaggedItems();
                    }
                })
        }
    }

    /*ITEM GROUP UI*/
    const [isGroupUiDialogOpen, setGroupUiDialog] = useState(false);
    const [groupUi, setGroupUi] = useState('');
    const isGroupUiValid = groupUiPattern.test(groupUi.trim().toUpperCase());

    const [nextGroupUi, setNextGroupUi] = useState('');
    const [nextGroupUiMessage, setNextGroupUiMessage] = useState('');

    const openGroupUiDialog = (item: FlaggedItem) => {
        setSelectedItem(item);
        setGroupUi(item.groupUi);
        setGroupUiDialog(true);
        getExcessIssueUis();
    }

    //Returns the filer's excess UIs for the audit year with the next available one last
    const getExcessIssueUis = () => {
        setNextGroupUi('');

        //Without the audit period the lookup finds no tags and always answers EXCESS-YYYY-1
        if (!props.cpfId || !props.auditPeriodId) {
            setNextGroupUiMessage('The next new group UI could not be looked up: this issue has no audit period.');
        }
        else {
            setNextGroupUiMessage('Looking up the next new group UI...');
            var params = [
                `cpfId=${props.cpfId}`,
                `year=${props.auditPeriodId.substring(0, 4)}`,
                `auditPeriodId=${encodeURIComponent(props.auditPeriodId)}`
            ].join('&');
            let url = `/DepositoryAudits/GetExcessIssueUIs?${params}`
            fetch(url)
                .then(result => result.json())
                .then(result => {
                    if (result.isError) {
                        setNextGroupUiMessage('The next new group UI could not be looked up.');
                        let info = {
                            type: 'error',
                            title: 'Error: GetExcessIssueUIs',
                            errorMessage: result.errorMessage,
                            secondaryErrorMessage: result.secondaryErrorMessage
                        };
                        setAlertInfo(info);
                        setAlertInfoDialog(true);
                    }
                    else {
                        let uis: string[] = result.result;
                        setNextGroupUiMessage('');
                        setNextGroupUi(uis[uis.length - 1]);
                    }
                })
        }
    }

    const closeGroupUiDialog = () => {
        setGroupUiDialog(false);
    }

    const saveGroupUi = () => {

        if (isGroupUiValid) {
            var params = [
                `flaggedItemId=${selectedItem?.flaggedItemId}`,
                `groupUi=${encodeURIComponent(groupUi.trim().toUpperCase())}`
            ].join('&');
            let url = `/Archer3/SaveFlaggedItemGroupUi?${params}`
            fetch(url)
                .then(result => result.json())
                .then(result => {
                    if (result.isError) {
                        let info = {
                            type: 'error',
                            title: 'Error: SaveFlaggedItemGroupUi',
                            errorMessage: result.errorMessage,
                            secondaryErrorMessage: result.secondaryErrorMessage
                        };
                        setAlertInfo(info);
                        setAlertInfoDialog(true);
                    }
                    else {
                        setGroupUiDialog(false);
                        props.updateFlaggedItems();
                    }
                })
        }
    }

    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }

    return (
        <>
            <TableContainer sx={{ maxHeight: 200 }}>
                <Table stickyHeader sx={{ minWidth: 450 }} size="small" aria-label="Flagged Items Table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Type</TableCell>
                            <TableCell align="right">Date</TableCell>
                            <TableCell align="right">Name</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell align="right"></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {props.flagItems.map((item) => (
                            <TableRow
                                key={item.flaggedItemId}
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                                <TableCell align="right">{item.recordTypeDescription}</TableCell>
                                <TableCell component="th" scope="row">
                                    {item.date}
                                </TableCell>
                                <TableCell align="right">{item.fullName}
                                    {item.groupUi &&
                                        <div><i>{item.groupUi}</i></div>}
                                </TableCell>
                                <TableCell align="right">{item.amount}</TableCell>
                                <TableCell align="right">
                                    <Stack direction="row" spacing={1}>
                                        {
                                            props.isGroupUiEditable && item.groupUi &&
                                            <IconButton color="primary" title="Edit the group UI" onClick={() => openGroupUiDialog(item)}>
                                                <DriveFileRenameOutline />
                                            </IconButton>
                                        }
                                        {
                                            !item.auditorNote &&
                                            <IconButton color="primary" title="Add an auditor note" onClick={(event) => openItemAuditNoteDialog(event, item)}>
                                                <EditNote />
                                            </IconButton>
                                        }
                                        {
                                            item.auditorNote &&
                                            <IconButton color="primary" title="View the auditor note" onClick={(event) => openItemAuditNoteDialog(event, item)}>
                                                <ChatBubbleOutline />
                                            </IconButton>
                                        }
                                        <Popper
                                            open={isAuditNoteDialogOpen}
                                            anchorEl={anchorEl}
                                        >
                                            <Paper sx={{ p: 2 }}>
                                                <TextField
                                                    label="Attach a note to the flagged item"
                                                    value={itemAuditNote}
                                                    size="small"
                                                    sx={{ width: 300 }}
                                                    onChange={(event) => {setItemAuditNote(event.target.value)}} 
                                                    InputProps={{
                                                        endAdornment: <Check color="success" className="is-clickable" onClick={saveItemAuditNote} />
                                                    }}
                                                />
                                            </Paper>
                                        </Popper>
                                        <IconButton color="error" title="Remove Item" onClick={() => { removeFlaggedItem(item.flaggedItemId) }}>
                                            <DeleteOutline />
                                        </IconButton>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Dialog open={isGroupUiDialogOpen} onClose={closeGroupUiDialog}>
                <DialogTitle>Edit Group UI</DialogTitle>
                <DialogContent>
                    {
                        nextGroupUi &&
                        <Typography variant="body2" sx={{ marginBottom: 1, maxWidth: 300, fontStyle: 'italic' }}>
                            The next new group UI is <b className="is-clickable" title="Use this group UI" onClick={() => setGroupUi(nextGroupUi)}>{nextGroupUi}</b>
                        </Typography>
                    }
                    {
                        !nextGroupUi && nextGroupUiMessage &&
                        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1, maxWidth: 300 }}>
                            {nextGroupUiMessage}
                        </Typography>
                    }
                    <TextField
                        autoFocus
                        label="Group UI"
                        value={groupUi}
                        size="small"
                        sx={{ width: 300, marginTop: 1 }}
                        error={!isGroupUiValid}
                        helperText={isGroupUiValid ? 'The group ui of the audit sheet tag for this item is updated too.' : 'Use the format EXCESS-2026-1.'}
                        onChange={(event) => { setGroupUi(event.target.value) }}
                        onKeyDown={(event) => { if (event.key === 'Enter') saveGroupUi(); }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeGroupUiDialog}>Cancel</Button>
                    <Button onClick={saveGroupUi} disabled={!isGroupUiValid}>Save</Button>
                </DialogActions>
            </Dialog>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
        </>
        )
}

export default FlagItemsTable